import { useEffect } from "react";
import { Shield, Key, AlertCircle } from "lucide-react";
import { usePermissions } from "@/context/PermissionsProvider";
import { useUiStore } from "@/store/uiStore";

const ACTION_STYLES: Record<string, string> = {
  create: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  delete: "bg-rose-50 text-rose-700 ring-rose-100",
  update: "bg-amber-50 text-amber-800 ring-amber-100",
  manage: "bg-violet-50 text-violet-700 ring-violet-100",
  view: "bg-sky-50 text-sky-700 ring-sky-100",
  start: "bg-cyan-50 text-cyan-700 ring-cyan-100",
  use: "bg-indigo-50 text-indigo-700 ring-indigo-100",
};

export function MyAccessTab() {
  const { me, roles, permissions, loading } = usePermissions();
  const { startPageLoading, stopPageLoading } = useUiStore();

  useEffect(() => {
    if (loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [loading, startPageLoading, stopPageLoading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-bridge-accent/20 border-t-bridge-accent" />
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex flex-col items-center gap-3 py-20 text-slate-400">
        <AlertCircle size={32} className="text-amber-400" />
        <p className="text-sm">Could not load your access profile.</p>
      </div>
    );
  }

  const grouped = [...permissions].reduce<Record<string, string[]>>((acc, perm) => {
    const [resource, action] = perm.split(":");
    (acc[resource] ||= []).push(action);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-bridge-accent/10 text-lg font-bold text-bridge-accent">
          {(me.name?.[0] ?? me.email[0]).toUpperCase()}
        </div>
        <div>
          <p className="font-semibold text-slate-900">{me.name || me.email}</p>
          <p className="text-sm text-slate-500">{me.email}</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-5 py-3">
          <Shield size={14} className="text-bridge-accent" />
          <span className="text-sm font-semibold text-slate-900">Assigned Roles</span>
        </div>
        <div className="flex flex-wrap gap-2 p-5">
          {roles.length === 0 && <p className="text-sm text-slate-400">No roles assigned.</p>}
          {roles.map((r) => (
            <span
              key={r}
              className="rounded-full bg-bridge-accent/10 px-3 py-1 text-sm font-semibold text-bridge-accent ring-1 ring-inset ring-bridge-accent/20"
            >
              {r}
            </span>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-5 py-3">
          <Key size={14} className="text-bridge-accent" />
          <span className="text-sm font-semibold text-slate-900">
            Effective Permissions ({permissions.size})
          </span>
        </div>
        <div className="space-y-4 p-5">
          {Object.keys(grouped).length === 0 && (
            <p className="text-sm text-slate-400">No permissions granted.</p>
          )}
          {Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([resource, actions]) => (
              <div key={resource} className="flex items-start gap-3">
                <span className="min-w-[140px] text-xs font-semibold capitalize text-slate-800">
                  {resource.replace(/_/g, " ")}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {actions.sort().map((action) => (
                    <span
                      key={action}
                      className={`rounded-md px-2 py-0.5 text-[11px] font-semibold capitalize ring-1 ring-inset ${
                        ACTION_STYLES[action] ??
                        "bg-slate-100 text-slate-600 ring-slate-200"
                      }`}
                    >
                      {action}
                    </span>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
